import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Ensure HTTPS in production
const getApiUrl = () => {
  const url = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  // Force HTTPS for all non-localhost URLs
  if (url && !url.includes('localhost') && !url.includes('127.0.0.1')) {
    return url.replace(/^http:\/\//, 'https://');
  }
  return url;
};

const API_URL = getApiUrl();

const AuthContext = createContext(null);

// Create axios instance with credentials
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  // 90s timeout: ID verification with OCR takes 5-15s typically, but slow cellular
  // connections + upload + LLM processing can spike. Avoid premature "Network error".
  timeout: 90000,
});

// Force HTTPS on all requests (interceptor)
api.interceptors.request.use((config) => {
  if (config.url && !config.url.includes('localhost') && !config.url.includes('127.0.0.1')) {
    config.url = config.url.replace(/^http:\/\//, 'https://');
  }
  if (config.baseURL && !config.baseURL.includes('localhost') && !config.baseURL.includes('127.0.0.1')) {
    config.baseURL = config.baseURL.replace(/^http:\/\//, 'https://');
  }
  return config;
});

// Utility to format API errors
export function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Build a more helpful message from any axios error
export function formatAxiosError(error, fallback) {
  if (error?.response) {
    const detail = error.response.data?.detail;
    if (detail != null) return formatApiErrorDetail(detail);
    const status = error.response.status;
    if (status === 413) return "Uploaded file is too large. Please reduce the image size and try again.";
    if (status === 502 || status === 504) return "The verification service is temporarily unavailable. Please try again in a moment.";
    if (status >= 500) return `Server error (${status}). Our team has been notified. Please try again or contact support.`;
    if (status === 401) return "Your session has expired. Please log in again.";
    if (status === 403) return "You don't have permission for this action.";
    if (status === 429) return "Too many requests. Please wait a moment and try again.";
    return error.response.data?.message || fallback || `Request failed with status ${status}.`;
  }
  if (error?.code === "ECONNABORTED" || error?.message?.includes("timeout")) {
    return "Request timed out. ID verification can be slow — please check your connection and try again.";
  }
  if (error?.message?.includes("Network Error")) {
    return "Network error. Please check your internet connection and try again.";
  }
  return error?.message || fallback || "Something went wrong. Please try again.";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));

  // Set token in axios header
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('access_token', token);
    } else {
      delete api.defaults.headers.common['Authorization'];
      localStorage.removeItem('access_token');
    }
  }, [token]);

  // Try to silently refresh the access token using the (httpOnly cookie or
  // last-known) refresh token. Returns true on success.
  const attemptSilentRefresh = useCallback(async () => {
    try {
      const { data } = await api.post('/api/auth/refresh');
      if (data?.token) {
        setToken(data.token);
        return true;
      }
      if (data?.access_token) {
        setToken(data.access_token);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  // Check auth on mount. If the access token is expired but a refresh token
  // still works, silently rotate it — the user should stay logged in until
  // they explicitly log out (PWA / mobile-app-like behaviour).
  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/api/auth/me');
      setUser(data);
    } catch (error) {
      // Access token missing/expired — attempt a silent refresh before
      // giving up. This keeps installed PWA sessions alive.
      const refreshed = await attemptSilentRefresh();
      if (refreshed) {
        try {
          const { data: me } = await api.get('/api/auth/me');
          setUser(me);
        } catch {
          setUser(false);
          setToken(null);
        }
      } else {
        setUser(false);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, [attemptSilentRefresh]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, options) => {
    try {
      // Registration flow
      if (options?.isRegister) {
        const { data } = await api.post('/api/auth/register', options.registerData);
        setToken(data.token);
        // Re-fetch full user (includes address_required flag etc.)
        try { const me = await api.get('/api/auth/me'); setUser(me.data); } catch { setUser(data.user); }
        return { success: true };
      }
      
      if (options?.totpCode) {
        // 2FA verification step - temp_token is passed as password param
        const { data } = await api.post(`/api/auth/login/2fa?temp_token=${encodeURIComponent(password)}&totp_code=${options.totpCode}`);
        setToken(data.token);
        try { const me = await api.get('/api/auth/me'); setUser(me.data); } catch { setUser(data.user); }
        return { success: true };
      }
      
      const { data } = await api.post('/api/auth/login', { email, password });
      
      if (data.requires_2fa) {
        return { success: false, requires_2fa: true, temp_token: data.temp_token };
      }
      
      setToken(data.token);
      try { const me = await api.get('/api/auth/me'); setUser(me.data); } catch { setUser(data.user); }
      return { success: true };
    } catch (error) {
      return { success: false, error: formatAxiosError(error, "Login or registration failed") };
    }
  };

  const register = async (userData) => {
    try {
      const { data } = await api.post('/api/auth/register', userData);
      setUser(data.user);
      setToken(data.token);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatAxiosError(error, "Registration failed") };
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(false);
      setToken(null);
    }
  };

  const refreshToken = async () => {
    try {
      const { data } = await api.post('/api/auth/refresh');
      setToken(data.access_token);
      return true;
    } catch (error) {
      setUser(false);
      setToken(null);
      return false;
    }
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/api/auth/me');
      setUser(data);
    } catch (error) {
      console.error('Refresh user failed:', error);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshToken,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isStaff: ['admin', 'moderator', 'cro'].includes(user?.role),
    api
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { api };
